<script lang="ts">
	import { tick } from 'svelte';

	interface Props {
		checked: boolean;
		/** Обязательна: переключатель без подписи непонятен без контекста. */
		label: string;
		disabled?: boolean;
		onchange: (checked: boolean) => void;
	}

	let { checked, label, disabled = false, onchange }: Props = $props();

	/**
	 * Переключатель показывает то, что говорит приложение, а не то, что нажали.
	 *
	 * Сам по себе флажок переворачивается от нажатия сразу. Если настройка при
	 * этом не сохранилась — сервер отказал или нажатие пришлось на идущее
	 * сохранение и было пропущено, — значение у приложения остаётся прежним, а
	 * Svelte не трогает разметку, пока оно не изменилось. Переключатель оставался
	 * «включённым» при выключенной настройке и врал до перезагрузки страницы.
	 *
	 * Поэтому после нажатия флажок возвращается к значению приложения. Там, где
	 * оно меняется сразу, это ничего не меняет; где сохранение идёт через сервер,
	 * переключатель перевернётся, когда тот подтвердит. Возврат происходит до
	 * отрисовки кадра, так что мигания нет.
	 */
	async function handle(event: Event & { currentTarget: HTMLInputElement }): Promise<void> {
		const input = event.currentTarget;
		onchange(input.checked);
		await tick();
		if (input.checked !== checked) input.checked = checked;
	}
</script>

<!--
	Настоящий <input type="checkbox">, а не div с ролью: так переключатель
	бесплатно получает поведение с клавиатуры, участие в форме и правильное
	озвучивание программами чтения с экрана.
-->
<label class="switch" class:disabled>
	<input type="checkbox" {checked} {disabled} aria-label={label} onchange={handle} />
	<span class="track"><span class="thumb"></span></span>
</label>

<style>
	.switch {
		display: inline-flex;
		align-items: center;
		flex-shrink: 0;
		cursor: pointer;
	}

	.switch.disabled {
		opacity: 0.45;
		cursor: default;
	}

	input {
		position: absolute;
		width: 1px;
		height: 1px;
		opacity: 0;
		pointer-events: none;
	}

	.track {
		position: relative;
		display: block;
		width: 44px;
		height: 26px;
		border-radius: var(--radius-round);
		background: var(--background-tertiary);
		transition: background var(--duration-fast) var(--ease-standard);
	}

	.thumb {
		position: absolute;
		top: 3px;
		left: 3px;
		width: 20px;
		height: 20px;
		border-radius: var(--radius-round);
		background: var(--text-primary);
		transition: transform var(--duration-normal) var(--ease-spring);
	}

	input:checked + .track {
		background: var(--accent-primary);
	}

	input:checked + .track .thumb {
		transform: translateX(18px);
		background: #fff;
	}

	/* Обводка нужна только при переходе с клавиатуры: мышью она мешает. */
	input:focus-visible + .track {
		outline: 2px solid var(--accent-primary);
		outline-offset: 2px;
	}
</style>
